/**
 * AdminPanel - 管理員特權面板
 * 
 * 提供管理員特權操作：
 * 1. 強制審批通過/拒絕
 * 2. 跳過當前步驟
 * 3. 重置流程狀態
 * 4. 修改任意審批人
 * 5. 查看完整審計日誌
 */

import React, { useState, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAdminActions, useNotificationStatus, useSendNotification } from '../../hooks/useApi';

/**
 * AdminPanel - 管理員特權操作面板
 * 
 * @param {Object} props
 * @param {string} props.applicationId - 申請單 ID
 * @param {Object} props.form - 表單數據
 * @param {Function} props.onActionComplete - 操作完成回調
 * @param {Function} props.getText - 多語言文字解析函數
 */
export default function AdminPanel({
    applicationId,
    form,
    onActionComplete,
    getText,
}) {
    const { t } = useI18n(['common', 'forms', 'admin']);
    const [isExpanded, setIsExpanded] = useState(false);
    const [actionComment, setActionComment] = useState('');
    const [confirmAction, setConfirmAction] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    
    // 催簽通知狀態
    const [notifyType, setNotifyType] = useState('both'); // 'email' | 'teams' | 'both'
    const [notifyLoading, setNotifyLoading] = useState(false);
    const [notifyResult, setNotifyResult] = useState(null);

    // Admin 操作 hooks (將在 useApi.js 中添加)
    const adminActions = useAdminActions();
    
    // 通知相關 hooks
    const { data: { data: notificationStatus } = {} } = useNotificationStatus();
    const sendNotification = useSendNotification();

    const approvalFlow = form?.approvalFlow || [];
    const currentStep = approvalFlow.find(s => s.status === 'current');
    const formStatus = form?.status || 'unknown';

    // 可用的管理員操作
    const availableActions = [
        {
            id: 'force_approve',
            label: t('admin:actions.forceApprove') || '強制核准',
            description: t('admin:actions.forceApproveDesc') || '跳過當前審批人直接核准',
            icon: 'check-circle',
            color: 'green',
            enabled: formStatus === 'pending' && currentStep,
            requireComment: false,
            confirmMessage: t('admin:confirm.forceApprove') || '確定要強制核准此申請嗎？',
        },
        {
            id: 'force_reject',
            label: t('admin:actions.forceReject') || '強制拒絕',
            description: t('admin:actions.forceRejectDesc') || '直接拒絕此申請',
            icon: 'x-circle',
            color: 'red',
            enabled: formStatus === 'pending' || formStatus === 'draft',
            requireComment: true,
            confirmMessage: t('admin:confirm.forceReject') || '確定要強制拒絕此申請嗎？',
        },
        {
            id: 'skip_step',
            label: t('admin:actions.skipStep') || '跳過步驟',
            description: t('admin:actions.skipStepDesc') || '跳過當前審批步驟',
            icon: 'fast-forward',
            color: 'yellow',
            enabled: formStatus === 'pending' && currentStep?.stepType === 'approval',
            requireComment: false,
            confirmMessage: t('admin:confirm.skipStep') || '確定要跳過此審批步驟嗎？',
        },
        {
            id: 'return_to_draft',
            label: t('admin:actions.returnToDraft') || '退回草稿',
            description: t('admin:actions.returnToDraftDesc') || '將申請退回草稿狀態',
            icon: 'arrow-left',
            color: 'orange',
            enabled: formStatus === 'pending' || formStatus === 'approved' || formStatus === 'rejected',
            requireComment: true,
            confirmMessage: t('admin:confirm.returnToDraft') || '確定要將此申請退回草稿嗎？',
        },
        {
            id: 'reset_flow',
            label: t('admin:actions.resetFlow') || '重置流程',
            description: t('admin:actions.resetFlowDesc') || '重置審批流程到初始狀態',
            icon: 'refresh',
            color: 'blue',
            enabled: true,
            requireComment: true,
            confirmMessage: t('admin:confirm.resetFlow') || '確定要重置審批流程嗎？所有審批記錄將被清除。',
        },
        {
            id: 'change_status',
            label: t('admin:actions.changeStatus') || '變更狀態',
            description: t('admin:actions.changeStatusDesc') || '直接變更申請單狀態',
            icon: 'edit',
            color: 'purple',
            enabled: true,
            requireComment: true,
            confirmMessage: t('admin:confirm.changeStatus') || '確定要變更申請單狀態嗎？',
        },
    ];

    // 執行管理員操作
    const handleAction = useCallback(async (action) => {
        if (action.requireComment && !actionComment.trim()) {
            setError(t('admin:errors.commentRequired') || '請輸入操作原因');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            await adminActions.mutateAsync({
                applicationId,
                action: action.id,
                comment: actionComment,
            });

            setConfirmAction(null);
            setActionComment('');
            onActionComplete?.();
        } catch (err) {
            setError(err.message || t('admin:errors.actionFailed') || '操作失敗');
        } finally {
            setIsLoading(false);
        }
    }, [applicationId, actionComment, adminActions, onActionComplete, t]);

    // 操作圖標
    const ActionIcon = ({ icon, color }) => {
        const colorClasses = {
            green: 'text-green-600',
            red: 'text-red-600',
            yellow: 'text-yellow-600',
            orange: 'text-orange-600',
            blue: 'text-blue-600',
            purple: 'text-purple-600',
        };

        const icons = {
            'check-circle': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            'x-circle': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            'fast-forward': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                </svg>
            ),
            'arrow-left': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
            ),
            'refresh': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
            ),
            'edit': (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
            ),
        };

        return (
            <span className={colorClasses[color] || 'text-gray-600'}>
                {icons[icon] || icons.edit}
            </span>
        );
    };

    return (
        <div className="bg-red-50 border border-red-200 rounded-lg overflow-hidden">
            {/* 標題欄 */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-4 py-3 flex items-center justify-between bg-red-100 hover:bg-red-200 transition-colors"
            >
                <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span className="font-medium text-red-800">
                        {t('admin:panel.title') || '管理員特權'}
                    </span>
                </div>
                <svg 
                    className={`w-5 h-5 text-red-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* 展開內容 */}
            {isExpanded && (
                <div className="p-4">
                    {/* 當前狀態信息 */}
                    <div className="mb-4 p-3 bg-white rounded-lg border border-red-100">
                        <div className="text-sm text-gray-600 mb-2">
                            {t('admin:panel.currentStatus') || '當前狀態'}
                        </div>
                        <div className="flex flex-wrap gap-2 text-sm">
                            <span className="px-2 py-1 bg-gray-100 rounded">
                                {t('admin:panel.formStatus') || '狀態'}: <strong>{t(`forms:status.${formStatus}`) || formStatus}</strong>
                            </span>
                            {currentStep && (
                                <span className="px-2 py-1 bg-gray-100 rounded">
                                    {t('admin:panel.currentStep') || '當前步驟'}: <strong>{getText ? getText(currentStep.stepName) : currentStep.stepName}</strong>
                                </span>
                            )}
                        </div>
                    </div>

                    {/* 操作按鈕列表 */}
                    <div className="space-y-2">
                        {availableActions.map(action => (
                            <button
                                key={action.id}
                                onClick={() => setConfirmAction(action)}
                                disabled={!action.enabled || isLoading}
                                className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition-colors ${
                                    action.enabled 
                                        ? 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50' 
                                        : 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                            >
                                <ActionIcon icon={action.icon} color={action.enabled ? action.color : 'gray'} />
                                <div className="flex-1">
                                    <div className={`font-medium ${action.enabled ? 'text-gray-900' : 'text-gray-400'}`}>
                                        {action.label}
                                    </div>
                                    <div className={`text-xs ${action.enabled ? 'text-gray-500' : 'text-gray-300'}`}>
                                        {action.description}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* 催簽通知區塊 */}
                    {formStatus === 'pending' && currentStep && (
                        <div className="mt-6 pt-4 border-t border-red-200">
                            <h4 className="font-medium text-red-800 mb-3 flex items-center gap-2">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                {t('admin:panel.reminderTitle') || '催簽通知'}
                            </h4>

                            {/* 當前審批人資訊 */}
                            <div className="mb-3 p-3 bg-white rounded-lg border border-gray-200">
                                <div className="text-sm text-gray-600 mb-2">
                                    {t('admin:panel.currentApprovers') || '當前待簽核人'}
                                </div>
                                <div className="space-y-1">
                                    {currentStep.approvers?.map((approver, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-medium">
                                                {approver.displayName?.[0] || '?'}
                                            </span>
                                            <span className="font-medium">{approver.displayName}</span>
                                            {approver.mail && (
                                                <span className="text-gray-400 text-xs">({approver.mail})</span>
                                            )}
                                        </div>
                                    )) || (
                                        <div className="text-gray-400 text-sm">{t('admin:panel.noApprovers') || '無審批人'}</div>
                                    )}
                                </div>
                            </div>

                            {/* 通知狀態指示器 */}
                            <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200 text-xs">
                                <div className="flex gap-4">
                                    <span className="flex items-center gap-1">
                                        <span className={`w-2 h-2 rounded-full ${notificationStatus?.smtp?.configured ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                        Email: {notificationStatus?.smtp?.configured ? t('admin:panel.ready') || 'Ready' : t('admin:panel.notConfigured') || 'Not Configured'}
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <span className={`w-2 h-2 rounded-full ${notificationStatus?.teams?.configured ? 'bg-green-500' : 'bg-red-500'}`}></span>
                                        Teams: {notificationStatus?.teams?.configured ? t('admin:panel.ready') || 'Ready' : t('admin:panel.notConfigured') || 'Not Configured'}
                                    </span>
                                </div>
                            </div>

                            {/* 發送方式選擇 */}
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('admin:panel.notifyType') || '發送方式'}
                                </label>
                                <div className="flex gap-2">
                                    {['email', 'teams', 'both'].map(type => (
                                        <button
                                            key={type}
                                            onClick={() => setNotifyType(type)}
                                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                                notifyType === type
                                                    ? 'bg-red-600 text-white'
                                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {type === 'email' ? 'Email' : type === 'teams' ? 'Teams' : t('admin:panel.notifyBoth') || '兩者'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* 發送催簽按鈕 */}
                            <button
                                onClick={async () => {
                                    if (!currentStep?.approvers?.length) {
                                        setNotifyResult({ success: false, message: t('admin:panel.noApproversToNotify') || '沒有可通知的審批人' });
                                        return;
                                    }
                                    setNotifyLoading(true);
                                    setNotifyResult(null);
                                    try {
                                        await sendNotification.mutateAsync({
                                            applicationId,
                                            type: notifyType,
                                            action: 'reminder', // 催簽動作
                                            template: 'approval_pending' // 使用正式的待審批模板
                                        });
                                        setNotifyResult({ 
                                            success: true, 
                                            message: t('admin:panel.reminderSent') || `已發送催簽通知給 ${currentStep.approvers.length} 位審批人` 
                                        });
                                    } catch (err) {
                                        setNotifyResult({ success: false, message: err.message });
                                    } finally {
                                        setNotifyLoading(false);
                                    }
                                }}
                                disabled={notifyLoading || !currentStep?.approvers?.length}
                                className="w-full px-4 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2"
                            >
                                {notifyLoading ? (
                                    <>
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        {t('admin:panel.sending') || '發送中...'}
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                        </svg>
                                        {t('admin:panel.sendReminder') || '發送催簽通知'}
                                    </>
                                )}
                            </button>

                            {/* 發送結果 */}
                            {notifyResult && (
                                <div className={`mt-3 p-2 rounded-lg text-sm ${
                                    notifyResult.success 
                                        ? 'bg-green-100 border border-green-300 text-green-700' 
                                        : 'bg-red-100 border border-red-300 text-red-700'
                                }`}>
                                    {notifyResult.message}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 錯誤提示 */}
                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}
                </div>
            )}

            {/* 確認對話框 */}
            {confirmAction && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
                        <div className="p-4 border-b border-gray-200">
                            <h3 className="font-medium text-gray-900 flex items-center gap-2">
                                <ActionIcon icon={confirmAction.icon} color={confirmAction.color} />
                                {confirmAction.label}
                            </h3>
                        </div>
                        <div className="p-4">
                            <p className="text-gray-600 mb-4">
                                {confirmAction.confirmMessage}
                            </p>

                            {confirmAction.requireComment && (
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('admin:panel.reason') || '操作原因'} *
                                    </label>
                                    <textarea
                                        value={actionComment}
                                        onChange={(e) => setActionComment(e.target.value)}
                                        placeholder={t('admin:panel.reasonPlaceholder') || '請輸入操作原因...'}
                                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                                        rows={3}
                                    />
                                </div>
                            )}

                            {error && (
                                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg text-red-700 text-sm">
                                    {error}
                                </div>
                            )}

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={() => {
                                        setConfirmAction(null);
                                        setActionComment('');
                                        setError(null);
                                    }}
                                    disabled={isLoading}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                >
                                    {t('common:cancel') || '取消'}
                                </button>
                                <button
                                    onClick={() => handleAction(confirmAction)}
                                    disabled={isLoading}
                                    className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${
                                        confirmAction.color === 'red' 
                                            ? 'bg-red-600 hover:bg-red-700' 
                                            : confirmAction.color === 'green'
                                            ? 'bg-green-600 hover:bg-green-700'
                                            : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                                >
                                    {isLoading 
                                        ? (t('common:processing') || '處理中...') 
                                        : (t('common:confirm') || '確認')
                                    }
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
